<script>
  import { onMount } from "svelte";
  import { onDestroy } from "svelte";

  let events = [];
  let filteredEvents = [];
  let uniqueCategories = [];
  let selectedCategories = [];
  let searchQuery = "";
  let dropdownEl;

  function getDuration(startTime, endTime) {
    if (!startTime || !endTime) return null;

    const start = new Date(`1970-01-01T${startTime}`);
    const end = new Date(`1970-01-01T${endTime}`);

    const diffMs = end - start;
    const diffMinutes = Math.floor(diffMs / 1000 / 60);
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    if (hours && minutes) return `${hours}h ${minutes}m`;
    if (hours) return `${hours} hour${hours > 1 ? "s" : ""}`;
    if (minutes) return `${minutes} minute${minutes > 1 ? "s" : ""}`;
    return null;
  }

  function getInitialEvents() {
    const eventElements = document.querySelectorAll(
      ".tribe-events-widget-events-list__event"
    );

    const events = Array.from(eventElements)
      .map((el) => {
        const article = el;
        const row = article.closest(
          ".tribe-events-widget-events-list__event-row"
        );

        const titleLink = el.querySelector(
          ".tribe-events-widget-events-list__event-title a"
        );
        const datetimeTag = row ? row.querySelector("time") : null;

        const startTimeNode = el.querySelector(".tribe-event-date-start");
        const endTimeNode = el.querySelector(".tribe-event-time");
        const statusNode = el.querySelector(".tribe-events-status-label__text");

        const startTime = startTimeNode
          ? startTimeNode.textContent.trim()
          : null;
        const endTime = endTimeNode ? endTimeNode.textContent.trim() : null;
        const statusText = statusNode ? statusNode.textContent.trim() : null;

        // Calculate duration if both times exist
        let duration = null;
        if (startTime && endTime) {
          const parseTime = (timeStr) => {
            const date = new Date(`1970-01-01T${convertTo24Hour(timeStr)}:00`);
            return isNaN(date) ? null : date;
          };

          const startDate = parseTime(startTime);
          const endDate = parseTime(endTime);

          if (startDate && endDate && endDate > startDate) {
            const diffMs = endDate - startDate;
            const diffMins = Math.floor(diffMs / 60000);
            const hours = Math.floor(diffMins / 60);
            const minutes = diffMins % 60;

            duration =
              hours > 0
                ? `${hours} hour ${minutes > 0 ? ` ${minutes} minutes ` : ""}`
                : `${minutes} minutes`;
          }
        }

        // Convert 12-hour time with AM/PM to 24-hour format
        function convertTo24Hour(timeStr) {
          const [time, modifier] = timeStr.split(" ");
          let [hours, minutes] = time.split(":");

          if (modifier === "PM" && hours !== "12") {
            hours = String(parseInt(hours, 10) + 12);
          }
          if (modifier === "AM" && hours === "12") {
            hours = "00";
          }

          return `${hours.padStart(2, "0")}:${minutes}`;
        }

        // Extract event ID
        const match = article.className.match(/post-(\d+)/);
        const id = match ? match[1] : null;

        // Extract category
        const categoryClass = Array.from(article.classList).find((cls) =>
          cls.startsWith("tribe_events_cat-")
        );
        const category = categoryClass
          ? categoryClass.replace("tribe_events_cat-", "")
          : null;

        return {
          id,
          title: titleLink ? titleLink.textContent.trim() : "",
          url: titleLink ? titleLink.href : "",
          date: datetimeTag ? datetimeTag.getAttribute("datetime") : "",
          startTime,
          endTime,
          status: statusText ? statusText : "Upcoming",
          category,
          duration,
          loadingExcerpt: true,
        };
      })
      .filter((event) => {
        const el = document.querySelector(`.post-${event.id}`);
        const isHidden = el && el.classList.contains("tribe-event-hidden");
        const isPublished = el && el.classList.contains("status-publish");
        return event.id && isPublished && !isHidden;
      });

    return events;
  }

  async function fetchEventWebsiteAndExcerpt(eventId) {
    try {
      const res = await fetch(
        `/wp-json/wp/v2/tribe_events/${eventId}?_fields=event_website,excerpt,content`
      );

      const data = await res.json();

      const website = data.event_website || null;

      // Try excerpt first
      let excerpt =
        data.excerpt && data.excerpt.rendered
          ? data.excerpt.rendered.trim()
          : "";

      // Fallback to trimmed content if excerpt is empty
      if (!excerpt || excerpt === "") {
        const content =
          data.content && data.content.rendered ? data.content.rendered : "";
        const textOnly = content.replace(/<[^>]*>?/gm, ""); // strip HTML
        const words = textOnly.split(/\s+/).slice(0, 55);
        excerpt = words.join(" ") + (words.length >= 55 ? "…" : "");
      }

      return { website, excerpt };
    } catch (err) {
      console.error(`Failed to fetch event ${eventId}`, err);
      return { website: null, excerpt: "" };
    }
  }

  function toggleCategory(category) {
    if (selectedCategories.includes(category)) {
      selectedCategories = selectedCategories.filter((c) => c !== category);
    } else {
      selectedCategories = [...selectedCategories, category];
    }
  }

  // Format category label
  const formatLabel = (cat) =>
    cat.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

  function formatDate(iso) {
    const date = new Date(iso);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  }

  function resetSelection() {
    selectedCategories = [];
  }

  function handleClickOutside(event) {
    if (dropdownEl && !dropdownEl.contains(event.target)) {
      dropdownEl.open = false;
    }
  }

  // Reactive filtered list
  $: filteredEvents = events.filter((event) => {
    const matchesCategory =
      selectedCategories.length === 0 ||
      (event.category &&
        selectedCategories.some((cat) => event.category.includes(cat)));

    const matchesSearch =
      !searchQuery ||
      (event.title &&
        event.title.toLowerCase().includes(searchQuery.toLowerCase()));

    return matchesCategory && matchesSearch;
  });

  onMount(async () => {
    events = getInitialEvents();

    // Fetch and attach event website URLs
    await Promise.all(
      events.map(async (event) => {
        const { website, excerpt } = await fetchEventWebsiteAndExcerpt(
          event.id
        );
        event.website = website;
        event.excerpt = excerpt;
        event.loadingExcerpt = false;
      })
    );

    // Unique categories
    uniqueCategories = Array.from(
      new Set(events.map((event) => event.category).filter(Boolean))
    );

    filteredEvents = events;

    console.log(events);

    document.addEventListener("click", handleClickOutside);
  });
  onDestroy(() => {
    document.removeEventListener("click", handleClickOutside);
  });
</script>

<div class="events-layout">
  <aside class="events-sidebar">
    <input
      type="text"
      class="search-input"
      placeholder="Search events by title..."
      bind:value={searchQuery}
    />

    <div class="filter-dropdown">
      <details class="dropdown" bind:this={dropdownEl}>
        <summary class="dropdown-summary">
          <span class="label">Category:</span>
          <span class="value">
            {selectedCategories.length > 0
              ? `${selectedCategories.length} Selected`
              : "All"}
          </span>
        </summary>

        <div class="dropdown-content">
          <header class="dropdown-header">
            <span>{selectedCategories.length} Selected</span>
            <button type="button" on:click={resetSelection}>Reset</button>
          </header>

          <ul class="category-list">
            {#each uniqueCategories as cat}
              <li>
                <label>
                  <input
                    type="checkbox"
                    value={cat}
                    checked={selectedCategories.includes(cat)}
                    on:change={() => toggleCategory(cat)}
                  />
                  <span>{formatLabel(cat)}</span>
                </label>
              </li>
            {/each}
          </ul>
        </div>
      </details>
    </div>
  </aside>

  <main class="events-main">
    {#if filteredEvents.length}
      <p class="events-count">
        Showing {filteredEvents.length}
        {filteredEvents.length === 1 ? "event" : "events"}
      </p>
      <div class="events-list">
        {#each filteredEvents as event}
          <div class="event-card">
            {#if event.category}
              <p class="event-category">{formatLabel(event.category)}</p>
            {/if}

            {#if event.title}
              <h3 class="event-title">
                <a href={event.url}>{event.title}</a>
              </h3>
            {/if}

            {#if event.excerpt}
              <p class="event-excerpt">{@html event.excerpt}</p>
            {:else if event.loadingExcerpt}
              <p class="event-excerpt loading">Loading event details…</p>
            {/if}

            <div class="event-meta">
              {#if event.status}
                <span class="badge">{event.status}</span>
              {/if}
              {#if event.date}
                <time class="event-time">
                  {formatDate(event.date)}
                  {event.startTime && ` ${event.startTime}`}
                  {event.endTime && `– ${event.endTime}`}
                </time>
              {/if}
              {#if event.duration}
                <span class="duration"
                  ><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"
                    ><g id="Layer_15" data-name="Layer 15"
                      ><path
                        d="M16 31a15 15 0 1 1 15-15 15 15 0 0 1-15 15Zm0-28a13 13 0 1 0 13 13A13 13 0 0 0 16 3Z"
                        style="fill: #101820;"
                      /><path
                        d="m20.24 21.66-4.95-4.95A1 1 0 0 1 15 16V8h2v7.59l4.66 4.65Z"
                        style="fill: #101820;"
                      /></g
                    ></svg
                  >{event.duration}</span
                >
              {/if}
            </div>
            {#if event.website}
              <a
                href={event.website}
                class="btn"
                target="_blank"
                rel="noreferrer noopener"
                aria-label="Event (opens in a new window)">Register ↗</a
              >
            {/if}
          </div>
        {/each}
      </div>
    {:else}
      <p>
        There are no upcoming events to display at this time. Please check again
        later.
      </p>
    {/if}
  </main>
</div>

<style>
  .events-layout {
    display: grid;
    grid-template-columns: 300px 1fr;
    gap: 2rem;
    align-items: start;
  }

  .events-sidebar {
    position: sticky;
    top: 3rem;
    align-self: start;
    background: #f9f9f9;
    border: 1px solid #eee;
    border-radius: 6px;
    padding: 1rem;
  }

  .search-input {
    width: 100%;
    padding: 10px 12px;
    font-size: 1rem;
    border: 1px solid #ccc;
    border-radius: 4px;
    margin-bottom: 1rem;
  }

  .events-main {
    min-width: 0;
  }

  .search-input {
    flex: 1 1 250px;
    padding: 10px 12px;
    font-size: 1rem;
    border: 1px solid #ccc;
    border-radius: 4px;
  }

  .filter-dropdown {
    position: relative;
    display: inline-block;
    width: 100%;
    max-width: 300px;
    font-family: sans-serif;
  }

  .dropdown summary {
    display: flex;
    justify-content: space-between;
    padding: 10px 15px;
    background: #fff;
    border: 1px solid #ccc;
    cursor: pointer;
    list-style: none;
  }

  .dropdown[open] summary {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  .dropdown-content {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    background: #fff;
    border: 1px solid #ccc;
    border-top: none;
    z-index: 10;
    border-radius: 0 0 4px 4px;
    max-height: 300px;
    overflow-y: auto;
  }

  .dropdown-header {
    display: flex;
    justify-content: space-between;
    padding: 10px;
    border-bottom: 1px solid #eee;
    background: #f9f9f9;
  }

  .category-list {
    list-style: none;
    padding: 10px;
    margin: 0;
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px 10px;
  }

  .category-list label {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 14px;
    line-height: 1;
    color: #333;
    cursor: pointer;
  }

  .category-list input[type="checkbox"] {
    width: 16px;
    height: 16px;
  }

  .events-list {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

  .events-count {
    position: relative;
    top: -3px;
    font-weight: 600;
    margin-bottom: 1rem;
    font-size: 1rem;
    line-height: 1;
    opacity: 0.3;
    color: #333;
  }

  .event-card {
    background-color: #f5f5f5;
    padding: 2rem;
    border-radius: 8px;
  }

  .event-category {
    font-weight: 600;
    color: #555;
    margin-bottom: 0.5rem;
  }

  .event-title a {
    font-size: 1.25rem;
    font-weight: bold;
    color: #003366;
    text-decoration: none;
  }

  .event-excerpt {
    margin: 0.75rem 0 1rem;
    color: #333;
  }

  .event-excerpt.loading {
    font-style: italic;
    color: #666;
    opacity: 0.7;
  }

  .event-meta {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .badge {
    background: #db2b69;
    color: #fff;
    padding: 7px 12px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: bold;
    text-transform: uppercase;
    margin: 1rem 0;
    line-height: 1;
  }

  .event-time {
    color: #222;
    font-weight: bold;
  }

  .duration {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 1rem;
    color: #555;
  }

  .duration svg {
    height: 25px;
    margin-right: 2px;
  }

  .btn {
    display: inline-block;
    color: #fff;
    background-color: #2854b7;
    padding: 16px 30px;
    border-radius: 30px;
    text-decoration: none;
    font-weight: bold;
    transition: background-color 0.15s;
  }

  .btn:hover {
    background-color: #3162d2;
  }

  :global(.tribe-compatibility-container) {
    margin-top: 2rem;
  }

  :global(.tribe-events-widget-events-list__header-title) {
    font-size: 24px !important;
    color: #0083de !important;
    font-weight: 700 !important;
  }

  :global(
      .tribe-events-widget-events-list__events,
      .tribe-events-widget-events-list__view-more
    ) {
    display: none;
  }

  @media (max-width: 1050px) {
    .event-meta {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5rem;
      margin-top: 0.75rem;
    }
    .badge {
      margin: 0 0 0.75rem;
    }
  }

  @media (max-width: 768px) {
    .events-layout {
      grid-template-columns: 1fr;
      gap: 0.5rem;
    }

    .events-sidebar {
      position: static;
      order: 1;
      margin-bottom: 2rem;
    }

    .filter-dropdown {
      /* width: 100%; */
      max-width: 100%;
    }

    .events-main {
      order: 2;
    }
  }
</style>
